import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

import type {
  ConductorConflictBlockedPayload,
  ConductorTimeoutFallbackPayload,
  ConversationCreatedPayload,
  ConversationPriorityChangedPayload,
  ConversationStatusChangedPayload,
  MessageDeliveryUpdatedPayload,
  MessageReceivedPayload,
  OperationModeChangedPayload,
  PlaybookTransitionExecutedPayload,
} from '../../../lib/websocket/events';

/**
 * useConversationEvents — assina eventos relevantes para listagem +
 * conversa específica (se `conversationId` informado) e invalida cache.
 *
 * Comportamento:
 *   - `conversation.message.received` → invalida `['messages', conversationId]`
 *     se for da conversa em foco; sempre invalida `['conversations']` para
 *     atualizar snippet + ordenação na listagem. Também sinaliza
 *     `hasNewMessage` que pode ser consumido para o indicador flutuante.
 *   - `conversation.created` → invalida `['conversations']` (entra na
 *     listagem com badge "Aguardando você" se aplicar).
 *   - `conversation.status.changed` → invalida `['conversation', id]` e
 *     `['conversations']`.
 *
 * Use no componente da tela de conversa (id em foco) e/ou no Layout para
 * receber criação de conversa nova.
 */
export interface PlaybookTransitionEvent {
  proposalId: string;
  fromPlaybookId: string | null;
  toPlaybookId: string;
  toPlaybookName: string;
  reason: string;
  executedAt: string;
}

export function useConversationEvents(conversationId: string | undefined): {
  /**
   * True quando chegou mensagem nova na conversa em foco enquanto o leitor
   * não estava no fundo da lista (consumir + resetar via `acknowledgeNewMessage`).
   */
  hasNewMessage: boolean;
  acknowledgeNewMessage: () => void;
  /**
   * Fatia 11.2 — última transição de playbook (banner sticky discreto).
   * Reset 8s após receber (UX não-intrusivo per Q3 decisão Tech Lead).
   */
  lastTransition: PlaybookTransitionEvent | null;
  dismissTransition: () => void;
} {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [lastTransition, setLastTransition] = useState<PlaybookTransitionEvent | null>(null);

  const dismissTransition = useCallback(() => setLastTransition(null), []);

  useEffect(() => {
    const unsubMessage = subscribe<MessageReceivedPayload>(
      WS_EVENTS.MESSAGE_RECEIVED,
      (envelope) => {
        const { conversationId: incomingId } = envelope.data;
        if (conversationId !== undefined && incomingId === conversationId) {
          void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
          setHasNewMessage(true);
        }
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      },
    );

    const unsubCreated = subscribe<ConversationCreatedPayload>(
      WS_EVENTS.CONVERSATION_CREATED,
      () => {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      },
    );

    const unsubStatus = subscribe<ConversationStatusChangedPayload>(
      WS_EVENTS.CONVERSATION_STATUS_CHANGED,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        void queryClient.invalidateQueries({ queryKey: ['conversation', targetId] });
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      },
    );

    // Fatia 6 PR-A — delivery status updates (sent → delivered → read / failed).
    const unsubDelivery = subscribe<MessageDeliveryUpdatedPayload>(
      WS_EVENTS.MESSAGE_DELIVERY_UPDATED,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        if (conversationId !== undefined && targetId === conversationId) {
          void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        }
      },
    );

    // Fatia 11.2 — F1 fix: mode change canonical (auto-refresh UI sem F5).
    const unsubMode = subscribe<OperationModeChangedPayload>(
      WS_EVENTS.OPERATION_MODE_CHANGED,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        if (conversationId !== undefined && targetId === conversationId) {
          void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
          void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
        }
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      },
    );

    // Fatia 11.2 — playbook transition silent (banner discreto sticky).
    const unsubTransition = subscribe<PlaybookTransitionExecutedPayload>(
      WS_EVENTS.PLAYBOOK_TRANSITION_EXECUTED,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        if (conversationId !== undefined && targetId === conversationId) {
          setLastTransition({
            proposalId: envelope.data.proposalId,
            fromPlaybookId: envelope.data.fromPlaybookId,
            toPlaybookId: envelope.data.toPlaybookId,
            toPlaybookName: envelope.data.toPlaybookName,
            reason: envelope.data.reason,
            executedAt: envelope.data.executedAt,
          });
          // Auto-dismiss banner após 8s (UX não-intrusivo Q3 Tech Lead).
          setTimeout(() => setLastTransition(null), 8000);
        }
      },
    );

    // Fatia 11.2 — timeout fallback (Item 257). Apenas invalida cache;
    // UI atualiza naturalmente via OPERATION_MODE_CHANGED já emitido em paralelo.
    const unsubTimeout = subscribe<ConductorTimeoutFallbackPayload>(
      WS_EVENTS.CONDUCTOR_TIMEOUT_FALLBACK,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        if (conversationId !== undefined && targetId === conversationId) {
          void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        }
      },
    );

    // Fatia 11.2 — conflict-blocked (Regulated pausa H_c).
    const unsubConflict = subscribe<ConductorConflictBlockedPayload>(
      WS_EVENTS.CONDUCTOR_CONFLICT_BLOCKED,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        if (conversationId !== undefined && targetId === conversationId) {
          void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        }
      },
    );

    // ADR-146 — prioridade operacional alterada por cron SLA (sem co-evento de mensagem).
    // Invalida a listagem (badge "Urgente" + ordenação) e o detalhe da conversa afetada.
    const unsubPriority = subscribe<ConversationPriorityChangedPayload>(
      WS_EVENTS.CONVERSATION_PRIORITY_CHANGED,
      (envelope) => {
        const { conversationId: targetId } = envelope.data;
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        void queryClient.invalidateQueries({ queryKey: ['conversation', targetId] });
      },
    );

    return () => {
      unsubMessage();
      unsubCreated();
      unsubStatus();
      unsubDelivery();
      unsubMode();
      unsubTransition();
      unsubTimeout();
      unsubConflict();
      unsubPriority();
    };
  }, [conversationId, queryClient, subscribe]);

  return {
    hasNewMessage,
    acknowledgeNewMessage: () => setHasNewMessage(false),
    lastTransition,
    dismissTransition,
  };
}
